import type { DistributiveOmit } from '@emotion/react';
import type { ButtonOrLinkElement } from '@common/ButtonOrLink';
import type { LinkProps } from '@components/Link/Link';
import { Link } from '@components/Link/Link';

export type LinkActionExtraProps<T extends ButtonOrLinkElement> =
  DistributiveOmit<
    LinkProps<T>,
    'as' | 'children' | 'onClick' | 'kind' | 'href'
  >;

export interface ConsumedActionProps<T extends string | undefined> {
  /**
   * Text to show in action link/button
   */
  actionText?: LinkProps<T extends string ? 'a' : 'button'>['children'];
  /**
   * Url for the action link.
   * If not set, a button will be rendered.
   */
  actionHref?: T;
  /**
   * Optional callback for action button.
   */
  onAction?: LinkProps<T extends string ? 'a' : 'button'>['onClick'];
  /**
   * Additional props for rendered action component
   */
  actionProps?: LinkActionExtraProps<T extends string ? 'a' : 'button'>;
}

export type NotificationActionProps = DistributiveOmit<LinkProps, 'as'> & {
  href?: string;
};

export type LinkActionPassedProps<T extends string | undefined> =
  DistributiveOmit<
    LinkProps<T extends undefined ? 'button' : 'a'>,
    'children' | 'href' | 'as'
  > & {
    href?: T;
    as: T extends undefined ? 'button' : 'a';
  };

export const NotificationAction = ({
  children,
  href,
  ...props
}: NotificationActionProps): JSX.Element => {
  let linkProps;
  if (href) {
    linkProps = {
      ...props,
      href,
      as: 'a',
    } as LinkActionPassedProps<string>;
  } else {
    linkProps = {
      ...props,
      as: 'button',
    } as LinkActionPassedProps<undefined>;
  }

  return (
    <>
      {' '}
      <Link
        {...linkProps}
        as={href ? 'a' : 'button'}
        data-testid="notification-action"
      >
        {children}
      </Link>
    </>
  );
};
